// Importing components
import { Accordion, Description, ConditionalComponent } from 'components';
import { Button } from 'components/form-elements';

// Importing Constants
import {
    CUSTOMER_AGENT_INFO_ENUM,
    MERCHANT_RESPONSE_INFO_ENUM,
} from './constant';

// Importing Libraries
import React from 'react';
import PropTypes from 'prop-types';

// Importing styles
import './chargeBackAccordion.scss';
import {
    CHARGEBACK_STATUS_ENUM,
    CHARGEBACK_TERMINAL_STATE,
    DISPUTE_TYPE,
} from '../../utils/constants/commonValues';

/**
 * ChargeBackAccordion Component
 * @param {Object} props - The props object containing chargebackDetails and chargebackIndex
 * @param {Object} props.chargebackDetails - Details of the chargeback
 * @param {number} props.chargebackIndex - Index of the chargeback
 * @returns {JSX.Element} ChargeBackAccordion component JSX
 */
const ChargeBackAccordion = ({
    chargebackDetails,
    chargebackIndex,
    handleChargebackExtensionClick,
    handleChargebackReRaiseClicked,
    handleChargebackFullyAccept,
    handleChargebackDefend,
    currentChargebackId,
    userDetails,
    disputeType
}) => {
    const {
        customerAgentData,
        merchantData,
        allowChargebackReRaise,
    } = chargebackDetails;

    const isMerchantActionVisibile =
        chargebackDetails.currentChargebackStatus ===
            CHARGEBACK_STATUS_ENUM.PENDING_RESPONSE.value ||
        chargebackDetails.currentChargebackStatus ===
            CHARGEBACK_STATUS_ENUM.INSUFFICIENT_DOCUMENT.value;

    let customerAgentResponseData = [];
    let merchantResponseData = [];

    // Generating customer agent response data
    if (customerAgentData) {
        customerAgentResponseData = CUSTOMER_AGENT_INFO_ENUM.map(item => {
            return {
                ...item,
                label : item.label.replace("DisputeType", disputeType),
                children: customerAgentData[item.key],
            };
        });
    }

    // Generating merchant response data
    if (merchantData) {
        merchantResponseData = MERCHANT_RESPONSE_INFO_ENUM.map(item => {
            return {
                ...item,
                children: merchantData[item.key],
            };
        });
    }

    return (
        <div className="chargeback-level-container">
            {/* Rendering Accordion component */}
            <Accordion
                customClass="rounded-accordion"
                isOpened={false}
                isArrowIndicator
                key={chargebackIndex}
                id="live-credentials-accordion"
                upperComponent={
                    <h3 className="page-tile sm">
                        <span
                            className={`heading ${
                                customerAgentData.daysRemaining &&
                                currentChargebackId ===
                                    chargebackDetails.levelChargebackId &&
                                !CHARGEBACK_TERMINAL_STATE.includes(
                                    chargebackDetails.currentChargebackStatus
                                )
                                    ? ''
                                    : 'hide-border'
                            }`}
                        >
                            {disputeType == DISPUTE_TYPE["FRAUD"] ? "Fraud Details" : `Chargeback Level - L${chargebackIndex + 1}`}{' '}
                        </span>
                        <span className="meta-information">
                            <ConditionalComponent
                                isVisible={
                                    customerAgentData.daysRemaining &&
                                    currentChargebackId ===
                                        chargebackDetails.levelChargebackId &&
                                    !CHARGEBACK_TERMINAL_STATE.includes(
                                        chargebackDetails.currentChargebackStatus
                                    )
                                }
                            >
                                <span className="subtext">
                                    {customerAgentData.daysRemaining} days
                                    remaining
                                </span>
                            </ConditionalComponent>

                            <ConditionalComponent
                                isVisible={
                                    customerAgentData.daysRemaining &&
                                    currentChargebackId ===
                                        chargebackDetails.levelChargebackId &&
                                    !CHARGEBACK_TERMINAL_STATE.includes(
                                        chargebackDetails.currentChargebackStatus
                                    ) &&
                                    userDetails?.userLevel === 'ADMIN'
                                }
                            >
                                <Button
                                    text="Extend CB Deadline"
                                    buttonType="secondary"
                                    customClass="extend-button"
                                    handleClick={() =>
                                        handleChargebackExtensionClick(
                                            chargebackDetails
                                        )
                                    }
                                />
                            </ConditionalComponent>
                        </span>
                    </h3>
                }
                lowerComponent={
                    <>
                        {/* Rendering Description component for customer/agent response */}
                        <Description
                            items={customerAgentResponseData}
                            title="Customer/Agent Response"
                        />

                        <ConditionalComponent
                            isVisible={
                                merchantResponseData &&
                                merchantResponseData.length &&
                                !(
                                    userDetails?.userLevel === 'MERCHANT' &&
                                    merchantData.status === 'Pending'
                                )
                            }
                        >
                            <>
                                <div className="separator" />
                                {/* Rendering Description component for merchant response */}
                                <Description
                                    items={merchantResponseData}
                                    title="Merchant Response"
                                />
                            </>
                        </ConditionalComponent>

                        <ConditionalComponent
                            isVisible={
                                allowChargebackReRaise &&
                                currentChargebackId ===
                                    chargebackDetails.levelChargebackId
                            }
                        >
                            <>
                                <div className="separator" />
                                {/* Rendering Re Raise component */}
                                <div className="action-controller">
                                    <div className="sub-heading">
                                        Take Action on {disputeType}
                                    </div>
                                    <Button
                                        text="Reraise CB"
                                        buttonType="secondary"
                                        customClass="extend-button"
                                        handleClick={() =>
                                            handleChargebackReRaiseClicked(
                                                chargebackDetails
                                            )
                                        }
                                    />
                                </div>
                            </>
                        </ConditionalComponent>

                        <ConditionalComponent
                            isVisible={
                                isMerchantActionVisibile &&
                                currentChargebackId ===
                                    chargebackDetails.levelChargebackId &&
                                userDetails?.userLevel === 'MERCHANT'
                            }
                        >
                            <div className="separator" />
                            {/* Rendering Re Raise component */}
                            <div className="action-controller">
                                <div className="sub-heading">
                                    Take Action on {disputeType}
                                </div>
                                <Button
                                    text="Accept"
                                    buttonType="primary"
                                    customClass="extend-button"
                                    handleClick={() =>
                                        handleChargebackFullyAccept(
                                            chargebackDetails
                                        )
                                    }
                                />
                                <Button
                                    text="Defend"
                                    buttonType="primary"
                                    customClass="extend-button"
                                    handleClick={() =>
                                        handleChargebackDefend(
                                            chargebackDetails
                                        )
                                    }
                                />
                            </div>
                        </ConditionalComponent>
                    </>
                }
            />
        </div>
    );
};

// PropType validation for ChargeBackAccordion component
ChargeBackAccordion.propTypes = {
    chargebackDetails: PropTypes.object,
    chargebackIndex: PropTypes.number,
    currentChargebackId: PropTypes.string,
    handleChargebackExtensionClick: PropTypes.func,
    handleChargebackReRaiseClicked: PropTypes.func,
    handleChargebackFullyAccept: PropTypes.func,
    handleChargebackDefend: PropTypes.func,
    userDetails: PropTypes.object,
};

export default ChargeBackAccordion;
